import { lazy } from 'react'
import { createRoutesFromElements, Navigate, Route, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AppLayout, SessionLayout, useRouteSession } from '@web/app'
import { PageContent } from '@layerit/ui/components/page-content'
import { Button } from '@layerit/ui/components/ui/button'
import { AuthLayout, authDestination } from '@web/layouts/auth-layout'
import { ConsoleLayout } from '@web/layouts/console-layout'
import { OnboardingLayout } from '@web/layouts/onboarding-layout'
import { CONNECTION_PAGES, NEW_SIGNATURE, ROUTES, RULE_PAGES, SETTINGS_PAGES, SIGNATURE_PAGES } from '@web/router'
import { rpc } from '@web/rpc'
import { AccountShell, OrganisationChooser } from '@web/screens/choose-organisation'
import { NameScreen } from '@web/screens/onboarding/name'
import { OrganisationScreen } from '@web/screens/onboarding/organisation'
import { NewOrganisationScreen } from '@web/screens/organisations/new'
import { SignInScreen } from '@web/screens/sign-in'
import { SignUpScreen } from '@web/screens/sign-up'
import { SignInVerify, SignUpVerify } from '@web/screens/auth/email-code/routes'

/* Every console screen is its own chunk, fetched the first time it is opened, so signing in
 * does not download the designer. `ConsoleLayout` holds the Suspense boundary. The auth and
 * onboarding screens stay in the entry chunk: they are the first thing a visitor sees. */
const AccountScreen = lazy(() => import('@web/screens/settings/account').then((module) => ({ default: module.AccountScreen })))
const AuditScreen = lazy(() => import('@web/screens/audit').then((module) => ({ default: module.AuditScreen })))
const ConnectionsScreen = lazy(() =>
	import('@web/screens/connections/connections').then((module) => ({ default: module.ConnectionsScreen })),
)
const ConnectionDirectoryScreen = lazy(() =>
	import('@web/screens/connections/connection-directory').then((module) => ({ default: module.ConnectionDirectoryScreen })),
)
const ConnectProviderScreen = lazy(() =>
	import('@web/screens/connections/connect').then((module) => ({ default: module.ConnectProviderScreen })),
)
const DesignerScreen = lazy(() => import('@web/screens/designer/designer').then((module) => ({ default: module.DesignerScreen })))
const DashboardScreen = lazy(() => import('@web/screens/dashboard/dashboard').then((module) => ({ default: module.DashboardScreen })))
const IdentitiesScreen = lazy(() => import('@web/screens/identities').then((module) => ({ default: module.IdentitiesScreen })))
const InvitationScreen = lazy(() => import('@web/screens/invitation').then((module) => ({ default: module.InvitationScreen })))
const JoinScreen = lazy(() => import('@web/screens/join').then((module) => ({ default: module.JoinScreen })))
const RuleEditorScreen = lazy(() => import('@web/screens/rules/rule-editor').then((module) => ({ default: module.RuleEditorScreen })))
const RulesScreen = lazy(() => import('@web/screens/rules/rules').then((module) => ({ default: module.RulesScreen })))
const SignaturesScreen = lazy(() => import('@web/screens/signatures/signatures').then((module) => ({ default: module.SignaturesScreen })))
const BillingSettingsScreen = lazy(() =>
	import('@web/screens/settings/billing').then((module) => ({ default: module.BillingSettingsScreen })),
)
const InvoicesScreen = lazy(() => import('@web/screens/settings/invoices').then((module) => ({ default: module.InvoicesScreen })))
const OrganisationSettingsScreen = lazy(() =>
	import('@web/screens/settings/organisation').then((module) => ({ default: module.OrganisationSettingsScreen })),
)
const CompareRolesScreen = lazy(() =>
	import('@web/screens/settings/compare-roles').then((module) => ({ default: module.CompareRolesScreen })),
)
const InviteUsersScreen = lazy(() => import('@web/screens/settings/invite-users').then((module) => ({ default: module.InviteUsersScreen })))
const RoleEditorScreen = lazy(() => import('@web/screens/settings/role-editor').then((module) => ({ default: module.RoleEditorScreen })))
const RolesSettingsScreen = lazy(() => import('@web/screens/settings/roles').then((module) => ({ default: module.RolesSettingsScreen })))
const TemplatesScreen = lazy(() => import('@web/screens/templates').then((module) => ({ default: module.TemplatesScreen })))
const TeamsScreen = lazy(() => import('@web/screens/teams').then((module) => ({ default: module.TeamsScreen })))

function SignInRoute() {
	const navigate = useNavigate()
	const { search } = useLocation()
	const { state } = useRouteSession()
	return (
		<SignInScreen
			error={state.status === 'anonymous' ? state.error : null}
			redirectTo={authDestination(ROUTES.signIn, search)}
			navigate={navigate}
		/>
	)
}

function SignUpRoute() {
	const navigate = useNavigate()
	const { search } = useLocation()
	return <SignUpScreen redirectTo={authDestination(ROUTES.signUp, search)} navigate={navigate} />
}

function DashboardRoute() {
	const { can } = useRouteSession()
	/* Assigning a template needs `templates:write`; the panels hide their writes for an
	 * Auditor rather than offering a button the API refuses. */
	return <DashboardScreen client={rpc} canWrite={can('templates:write')} />
}

function SignaturesRoute() {
	const { can } = useRouteSession()
	return <SignaturesScreen client={rpc} canWrite={can('templates:write')} />
}

function DesignerRoute() {
	const { can, state } = useRouteSession()
	const navigate = useNavigate()
	const { id = NEW_SIGNATURE } = useParams()
	return (
		<DesignerScreen
			client={rpc}
			signatureId={id}
			navigate={(to, options) => void navigate(to, options)}
			canWrite={can('templates:write')}
			canPublish={can('templates:publish')}
			draftScope={state.status === 'ready' ? `${state.user.tenantId}:${state.user.id}` : null}
		/>
	)
}

function TemplatesRoute() {
	const { can } = useRouteSession()
	return <TemplatesScreen client={rpc} canPublish={can('templates:publish')} />
}

function RulesRoute() {
	const { can } = useRouteSession()
	return <RulesScreen client={rpc} canWrite={can('templates:write')} />
}

function RuleEditorRoute() {
	const { can } = useRouteSession()
	return <RuleEditorScreen client={rpc} canWrite={can('templates:write')} />
}

function ConnectionsRoute() {
	const { can } = useRouteSession()
	return <ConnectionsScreen client={rpc} canWrite={can('connections:write')} />
}

function ConnectionDirectoryRoute() {
	const { can } = useRouteSession()
	return <ConnectionDirectoryScreen client={rpc} canWrite={can('connections:write')} />
}

function ConnectProviderRoute() {
	const { can } = useRouteSession()
	return <ConnectProviderScreen client={rpc} canWrite={can('connections:write')} />
}

function AccountRoute() {
	const { state } = useRouteSession()
	if (state.status !== 'ready') return null
	return <AccountScreen user={state.user} permissions={state.permissions} client={rpc} />
}

function NotFoundRoute() {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	return (
		<PageContent className="gap-4">
			<header className="flex flex-col gap-1">
				<h1 className="font-display text-xl text-ink">Not found</h1>
				<p className="text-sm text-ink-3">
					<code className="font-mono">{pathname}</code> is not a screen in this console.
				</p>
			</header>
			<Button type="button" variant="primary" className="w-fit" onClick={() => void navigate(ROUTES.signatures)}>
				Back to signatures
			</Button>
		</PageContent>
	)
}

export const routeElements = createRoutesFromElements(
	<Route element={<SessionLayout />}>
		<Route element={<AppLayout />}>
			<Route element={<ConsoleLayout />}>
				<Route path={ROUTES.getStarted} index element={<DashboardRoute />} />
				<Route path={ROUTES.signatures} element={<SignaturesRoute />} />
				<Route path={SIGNATURE_PAGES.edit} element={<DesignerRoute />} />
				<Route path={ROUTES.templates} element={<TemplatesRoute />} />
				<Route path={ROUTES.rules} element={<RulesRoute />} />
				<Route path={RULE_PAGES.new} element={<RuleEditorRoute />} />
				<Route path={RULE_PAGES.edit} element={<RuleEditorRoute />} />
				<Route path={ROUTES.identities} element={<IdentitiesScreen client={rpc} />} />
				<Route path={ROUTES.connections} element={<ConnectionsRoute />} />
				<Route path={CONNECTION_PAGES.connect} element={<ConnectProviderRoute />} />
				<Route path={CONNECTION_PAGES.directory} element={<ConnectionDirectoryRoute />} />
				<Route path={ROUTES.invitation} element={<InvitationScreen />} />
				<Route path={ROUTES.join} element={<JoinScreen />} />

				{/* Settings: one route per sidebar item, which is the composition pinned for this
				 * surface. `/settings` is not a screen — it sends the reader to the first
				 * destination rather than showing an index whose only job is to be clicked. */}
				<Route path={ROUTES.settings} element={<Navigate to={ROUTES.settingsAccount} replace />} />
				<Route path={ROUTES.settingsAccount} element={<AccountRoute />} />
				<Route path={ROUTES.settingsOrganisation} element={<OrganisationSettingsScreen client={rpc} />} />
				<Route path={ROUTES.settingsBilling} element={<BillingSettingsScreen client={rpc} />} />
				<Route path={ROUTES.settingsUsers} element={<TeamsScreen client={rpc} />} />
				<Route path={ROUTES.settingsRoles} element={<RolesSettingsScreen client={rpc} />} />
				{/* Forms that are pages of their own, inside the destination that owns them. */}
				<Route path={SETTINGS_PAGES.usersInvite} element={<InviteUsersScreen client={rpc} />} />
				<Route path={SETTINGS_PAGES.billingInvoices} element={<InvoicesScreen />} />
				<Route path={SETTINGS_PAGES.rolesCompare} element={<CompareRolesScreen client={rpc} />} />
				<Route path={SETTINGS_PAGES.roleNew} element={<RoleEditorScreen client={rpc} mode="create" />} />
				<Route path={SETTINGS_PAGES.roleEdit} element={<RoleEditorScreen client={rpc} mode="edit" />} />
				<Route path={ROUTES.settingsAudit} element={<AuditScreen client={rpc} />} />

				{/* The three screens that moved under Settings keep their old addresses as redirects.
				 * They are in bookmarks and in links already sent, and a 404 is a poor way to
				 * learn that a page was reorganised. */}
				<Route path={ROUTES.account} element={<Navigate to={ROUTES.settingsAccount} replace />} />
				<Route path={ROUTES.team} element={<Navigate to={ROUTES.settingsUsers} replace />} />
				<Route path={ROUTES.audit} element={<Navigate to={ROUTES.settingsAudit} replace />} />
				<Route path={ROUTES.designer} element={<Navigate to={ROUTES.signatures} replace />} />
			</Route>

			<Route element={<AccountShell />}>
				<Route path={ROUTES.chooseOrganisation} element={<OrganisationChooser />} />
				<Route path={ROUTES.newOrganisation} element={<NewOrganisationScreen />} />
			</Route>

			<Route element={<OnboardingLayout />}>
				<Route path={ROUTES.onboardingName} element={<NameScreen />} />
				<Route path={ROUTES.onboardingOrganisation} element={<OrganisationScreen />} />
			</Route>

			<Route element={<ConsoleLayout />}>
				<Route path="*" element={<NotFoundRoute />} />
			</Route>
		</Route>

		<Route element={<AuthLayout />}>
			<Route path={ROUTES.signIn} element={<SignInRoute />} />
			<Route path={ROUTES.signInVerify} element={<SignInVerify />} />
			<Route path={ROUTES.signUp} element={<SignUpRoute />} />
			<Route path={ROUTES.signUpVerify} element={<SignUpVerify />} />
		</Route>
	</Route>,
)
